import apiClient from './api'
import type {
  IntegrationConfig,
  IntegrationConfigCreate,
  IntegrationConfigUpdate,
  ValidationResult,
  HealthStatus,
  IntegrationAnalytics,
  NotificationTemplate,
  NotificationTemplateCreate,
  NotificationTemplateUpdate,
  TemplatePreview,
  NotificationSendRequest,
  NotificationResponse,
  ServiceType,
  WebhookLog,
  WebhookFilters,
  WebhookSimulatorRequest,
  WebhookSimulatorResponse,
  WebhookURL,
  ConfigurationAuditLog,
  ConfigurationAuditFilters,
  ConfigurationVersion,
  ConfigurationVersionHistory,
  RollbackRequest,
  RollbackResult
} from '../types/integration'

// Integration Configuration APIs
export const integrationService = {
  // Get all integrations
  getAllIntegrations: async (params?: { service_type?: ServiceType; active_only?: boolean }): Promise<{ configs: IntegrationConfig[]; total: number }> => {
    const response = await apiClient.get('/admin/integrations/configs', { params })
    return response.data
  },

  // Get integrations by service type
  getIntegrationsByType: async (serviceType: ServiceType, activeOnly: boolean = true): Promise<{ configs: IntegrationConfig[]; total: number }> => {
    const response = await apiClient.get('/admin/integrations/configs', {
      params: { service_type: serviceType, active_only: activeOnly }
    })
    return response.data
  },

  // Get single integration
  getIntegration: async (id: string): Promise<IntegrationConfig> => {
    const response = await apiClient.get(`/admin/integrations/configs/${id}`)
    return response.data
  },

  // Create integration
  createIntegration: async (data: IntegrationConfigCreate): Promise<IntegrationConfig> => {
    const response = await apiClient.post('/admin/integrations/configs', data)
    return response.data
  },

  // Update integration
  updateIntegration: async (id: string, data: IntegrationConfigUpdate): Promise<IntegrationConfig> => {
    const response = await apiClient.put(`/admin/integrations/configs/${id}`, data)
    return response.data
  },

  // Delete integration
  deleteIntegration: async (id: string): Promise<void> => {
    await apiClient.delete(`/admin/integrations/configs/${id}`)
  },

  // Validate integration configuration (without saving)
  validateIntegration: async (data: { service_type: ServiceType; provider_name: string; config: Record<string, any> }): Promise<{ is_valid: boolean; error_message?: string }> => {
    const response = await apiClient.post('/admin/integrations/configs/validate', data)
    return response.data
  },

  // Test connection for existing integration
  testConnection: async (id: string): Promise<{ success: boolean; message: string; health_status?: string; details?: Record<string, any>; checked_at?: string }> => {
    const response = await apiClient.post(`/admin/integrations/health/${id}/check`)
    return response.data
  },

  // Health check APIs
  getAllHealth: async (params?: { service_type?: ServiceType; health_status?: string; is_active?: boolean }): Promise<{ integrations: HealthStatus[]; total: number; healthy_count: number; error_count: number; inactive_count: number }> => {
    const response = await apiClient.get('/admin/integrations/health', { params })
    return response.data
  },

  getIntegrationHealth: async (id: string): Promise<HealthStatus> => {
    const response = await apiClient.get(`/admin/integrations/health/${id}`)
    return response.data
  },

  checkIntegrationHealth: async (id: string): Promise<{ success: boolean; health_status?: string; message: string; details?: Record<string, any>; checked_at: string }> => {
    const response = await apiClient.post(`/admin/integrations/health/${id}/check`)
    return response.data
  },

  // Set integration priority
  setIntegrationPriority: async (id: string, priority: number): Promise<IntegrationConfig> => {
    const response = await apiClient.put(`/admin/integrations/configs/${id}`, { priority })
    return response.data
  },

  // Set as primary integration
  setAsPrimary: async (id: string): Promise<IntegrationConfig> => {
    const response = await apiClient.put(`/admin/integrations/configs/${id}`, { is_primary: true })
    return response.data
  },

  // Notification Template APIs
  getTemplates: async (params?: { channel?: string; event_type?: string }): Promise<NotificationTemplate[]> => {
    const response = await apiClient.get('/admin/notification-templates', { params })
    // Backend returns {total, items, limit, offset}, extract items array
    return response.data.items || response.data
  },

  getTemplate: async (id: string): Promise<NotificationTemplate> => {
    const response = await apiClient.get(`/admin/notification-templates/${id}`)
    return response.data
  },

  createTemplate: async (data: NotificationTemplateCreate): Promise<NotificationTemplate> => {
    const response = await apiClient.post('/admin/notification-templates', data)
    return response.data
  },

  updateTemplate: async (id: string, data: NotificationTemplateUpdate): Promise<NotificationTemplate> => {
    const response = await apiClient.put(`/admin/notification-templates/${id}`, data)
    return response.data
  },

  deleteTemplate: async (id: string): Promise<void> => {
    await apiClient.delete(`/admin/notification-templates/${id}`)
  },

  previewTemplate: async (id: string, sampleData: Record<string, any>): Promise<TemplatePreview> => {
    const response = await apiClient.post(`/admin/notification-templates/${id}/preview`, { sample_data: sampleData })
    return response.data
  },

  testTemplate: async (id: string, recipient: string, sampleData: Record<string, any>): Promise<NotificationResponse> => {
    const response = await apiClient.post(`/admin/notification-templates/${id}/test`, {
      recipient_contact: recipient,
      sample_data: sampleData
    })
    return response.data
  },

  // Send Notifications
  sendNotification: async (data: NotificationSendRequest): Promise<NotificationResponse> => {
    const response = await apiClient.post('/admin/notifications/send', data)
    return response.data
  },

  sendBulkNotification: async (data: NotificationSendRequest & { recipients: string[] }): Promise<NotificationResponse[]> => {
    const response = await apiClient.post('/admin/notifications/send-bulk', data)
    return response.data
  },

  // Webhook Management APIs
  getWebhookURLs: async (): Promise<WebhookURL[]> => {
    const response = await apiClient.get('/admin/webhooks/urls')
    return response.data
  },

  getWebhookLogs: async (filters?: WebhookFilters): Promise<{ logs: WebhookLog[]; total: number }> => {
    const response = await apiClient.get('/admin/webhooks/logs', { params: filters })
    return response.data
  },

  getWebhookLog: async (id: string): Promise<WebhookLog> => {
    const response = await apiClient.get(`/admin/webhooks/logs/${id}`)
    return response.data
  },

  simulateWebhook: async (data: WebhookSimulatorRequest): Promise<WebhookSimulatorResponse> => {
    const response = await apiClient.post('/admin/webhooks/simulate', data)
    return response.data
  },

  retryWebhook: async (id: string): Promise<WebhookSimulatorResponse> => {
    const response = await apiClient.post(`/admin/webhooks/logs/${id}/retry`)
    return response.data
  },

  // Configuration Audit APIs
  getConfigurationAuditLogs: async (filters?: ConfigurationAuditFilters): Promise<{ logs: ConfigurationAuditLog[]; total: number }> => {
    const response = await apiClient.get('/admin/integrations/audit-logs', { params: filters })
    return response.data
  },

  getConfigurationAuditLog: async (id: string): Promise<ConfigurationAuditLog> => {
    const response = await apiClient.get(`/admin/integrations/audit-logs/${id}`)
    return response.data
  },

  exportConfigurationAuditLogs: async (filters?: ConfigurationAuditFilters, format: 'csv' | 'pdf' = 'csv'): Promise<Blob> => {
    const response = await apiClient.get('/admin/integrations/audit-logs/export', {
      params: { ...filters, format },
      responseType: 'blob'
    })
    return response.data
  },

  // Configuration Version History APIs
  getConfigurationVersionHistory: async (configId: number): Promise<ConfigurationVersionHistory> => {
    const response = await apiClient.get(`/admin/integrations/${configId}/versions`)
    return response.data
  },

  getConfigurationVersion: async (configId: number, versionId: string): Promise<ConfigurationVersion> => {
    const response = await apiClient.get(`/admin/integrations/${configId}/versions/${versionId}`)
    return response.data
  },

  rollbackConfiguration: async (configId: number, data: RollbackRequest): Promise<RollbackResult> => {
    const response = await apiClient.post(`/admin/integrations/${configId}/rollback`, data)
    return response.data
  },

  // Analytics APIs
  getAnalytics: async (params?: { service_type?: ServiceType; start_date?: string; end_date?: string }): Promise<{
    analytics: {
      total_requests: number
      successful_requests: number
      failed_requests: number
      average_response_time: number
      success_rate: number
    }
    integrations: IntegrationAnalytics[]
    top_errors: Array<{ error_message: string; count: number }>
  }> => {
    const response = await apiClient.get('/admin/integrations/analytics', { params })
    return response.data
  },

  exportAnalytics: async (params?: { service_type?: ServiceType; start_date?: string; end_date?: string }, format: 'csv' | 'pdf' = 'csv'): Promise<Blob> => {
    const response = await apiClient.get('/admin/integrations/analytics/export', {
      params: { ...params, format },
      responseType: 'blob'
    })
    return response.data
  }
}

export default integrationService
